import moment, { Moment } from 'moment-timezone';

import { getLatestDateAndTime } from '../../../shared_components/utils/charting';

import {
  MARKET_DATA_DATE,
  MARKET_DATA_TIME_AND_DATE3,
  MARKET_DATA_TIME2,
} from '../../constants/date-time.constants';
import { CharacterEnum } from '../../enums/character.enum';
import { NonWorkingDaysResponse, StockExchangeCodeType } from '../../models/fundamental/types';
import { GWMarketDataSummaryBucket } from '../../models/gateway/types';
import { MarketStartAndEndTime, OHLCV, StartAndEndDate, TimeOfDay, TimeOfDayRange } from '../../models/market-data/types';
import { isEnhancedChartsEnabled } from '../../store/market-data/charting/chartFeatures';
import MarketHoursState from '../../store-util/MarketHoursState';
import TradePriceCache from '../../store-util/TradePriceCache';
import { isDateMarketDataWorkingDay } from '../../util/DateTimeHelpers';
import { isEmpty } from '../../util/typeGuards';
import { NullableNumber, NullableString } from '../../util/types';

import {
  BUCKET_LOOKBACK_CONFIG,
  DAYS_FROM_SUNDAY_TO_NEXT_MONDAY,
  DAYS_FROM_SUNDAY_TO_PREVIOUS_MONDAY,
  DEFAULT_MARKET_TIME,
  DEFAULT_OHLCV_DATA,
  FIRST_DAY_OF_MONTH,
  MAX_SYMBOLS_PER_REQUEST_LIMIT,
  MONDAY_DAY_INDEX,
  MONTH_BUCKET_MINUTES_BEFORE_OPEN,
  MONTH_INTERVAL_MINUTES,
  SUNDAY_DAY_INDEX,
  WEEK_INTERVAL_MINUTES,
  WEEK_OFFSET_FOR_NEXT_MONDAY,
  YEAR_INTERVAL_DAYS,
} from './constants';

export const chunkSymbols = (symbols: string[]): string[][] => {
  const chunks: string[][] = [];
  for (let i = 0; i < symbols.length; i += MAX_SYMBOLS_PER_REQUEST_LIMIT) {
    chunks.push(symbols.slice(i, i + MAX_SYMBOLS_PER_REQUEST_LIMIT));
  }
  return chunks;
};

export const buildMomentByDateAndTime = (
  baseDate: Moment,
  timeZone: string,
  hour: number,
  minute: number,
): Moment => baseDate.clone().tz(timeZone).hour(hour).minute(minute)
  .second(0)
  .millisecond(0)
  .utc();

export const buildMomentByDateAndTimeZone = (baseDate: Moment, timeZone: string): Moment => {
  const currDateMoment = moment().tz(timeZone);
  return buildMomentByDateAndTime(baseDate, timeZone, currDateMoment.hour(), currDateMoment.minute());
};

export const hasSessionStartedToday = ({ timeZone, start }: MarketStartAndEndTime): boolean => {
  const currDate = moment.tz(timeZone);
  const sessionStart = buildMomentByDateAndTime(currDate, timeZone, start.hour, start.minute);

  return currDate.isSameOrAfter(sessionStart);
};

export const shouldIncludeToday = (country: string): boolean => (
  hasSessionStartedToday(getTradingSessionConfig(country, undefined))
);

export const getSessionHoursForDate = (date: Moment, sessionConfig: MarketStartAndEndTime): TimeOfDayRange => {
  const { start, end, endForHalfDay, nonWorkingDays } = sessionConfig;
  const dateString = date.format(MARKET_DATA_DATE);
  const isHalfDay = nonWorkingDays.some(day => day.date === dateString && day.halfDay);

  return { start, end: isHalfDay ? endForHalfDay : end };
};

export const getMarketHoursByStockExchangeCode = (
  date: Moment,
  country: string,
  minutesBeforeOpen?: NullableNumber,
): TimeOfDayRange => getSessionHoursForDate(date, getTradingSessionConfig(country, minutesBeforeOpen));

export const createFilledCandle = (data: OHLCV, timeMoment: Moment): OHLCV => ({
  ...data,
  volume: 0,
  dateAndTime: timeMoment.format(MARKET_DATA_TIME_AND_DATE3),
  date: timeMoment.format(MARKET_DATA_DATE),
  time: timeMoment.format(MARKET_DATA_TIME2),
});

export const convertOHLCVToMap = (data: OHLCV[], keyField: 'dateAndTime' | 'date'): Map<string, OHLCV> => {
  const dataMap = new Map<string, OHLCV>();
  data.forEach(item => {
    const key = item[keyField];
    if (key) {
      dataMap.set(key, item);
    }
  });

  return dataMap;
};

export const findNextMondayOrCurrent = (date: Moment): Moment => {
  const dayOfWeek = date.day();
  if (dayOfWeek === MONDAY_DAY_INDEX) {
    return date.clone();
  }

  const daysUntilMonday = dayOfWeek === SUNDAY_DAY_INDEX
    ? DAYS_FROM_SUNDAY_TO_NEXT_MONDAY
    : (WEEK_OFFSET_FOR_NEXT_MONDAY - dayOfWeek);

  return date.clone().add(daysUntilMonday, 'days');
};

export const findPreviousMondayOrCurrent = (date: Moment): Moment => {
  const dayOfWeek = date.day();

  if (dayOfWeek === MONDAY_DAY_INDEX) {
    return date.clone();
  }

  if (dayOfWeek === SUNDAY_DAY_INDEX) {
    return date.clone().subtract(DAYS_FROM_SUNDAY_TO_PREVIOUS_MONDAY, 'days');
  }

  return date.clone().subtract(dayOfWeek - MONDAY_DAY_INDEX, 'days');
};

export const getStartMondayForRange = (symbolRows: OHLCV[], timeZone: string, calculatedStartDay: Moment): Moment => {
  if (symbolRows.length === 0) {
    return findNextMondayOrCurrent(calculatedStartDay);
  }

  const firstItemDate = symbolRows[0].date;
  if (!firstItemDate) {
    return findNextMondayOrCurrent(calculatedStartDay);
  }

  const firstItemDateMoment = moment.tz(firstItemDate, timeZone);
  const firstItemDateMonday = findPreviousMondayOrCurrent(firstItemDateMoment);

  return firstItemDateMonday.isBefore(calculatedStartDay)
    ? firstItemDateMonday
    : findNextMondayOrCurrent(calculatedStartDay);
};

export const getWeeklyStartMonday = (symbolRows: OHLCV[], timeZone: string, calculatedStartDay: Moment): Moment => {
  if (symbolRows.length === 0) {
    return calculatedStartDay.clone().date(FIRST_DAY_OF_MONTH);
  }

  const firstItemDate = symbolRows[0].date;
  if (!firstItemDate) {
    return calculatedStartDay.clone().date(FIRST_DAY_OF_MONTH);
  }

  const firstItemDateMoment = moment.tz(firstItemDate, timeZone);
  const firstItemDateMomentMonth = firstItemDateMoment.clone().date(FIRST_DAY_OF_MONTH);

  return firstItemDateMomentMonth.isBefore(calculatedStartDay)
    ? firstItemDateMomentMonth
    : calculatedStartDay.clone().date(FIRST_DAY_OF_MONTH);
};

export const parseMarketHourMinute = (value: NullableString, minutesBeforeOpen?: NullableNumber): TimeOfDay => {
  if (!value) {
    return DEFAULT_MARKET_TIME;
  }

  const [ hourStr, minuteStr ] = value.split(CharacterEnum.Colon);
  const hour = Number(hourStr);
  let minute = Number(minuteStr);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return DEFAULT_MARKET_TIME;
  }

  if (minutesBeforeOpen && minutesBeforeOpen >= minute) {
    minute -= minutesBeforeOpen;
  }

  return { hour, minute };
};

const buildTradingSessionConfig = (
  { marketHours, nonWorkingDays }: NonWorkingDaysResponse,
  minutesBeforeOpen?: NullableNumber,
): MarketStartAndEndTime => ({
  timeZone: marketHours.timeZone,
  nonWorkingDays,
  start: parseMarketHourMinute(marketHours?.open, minutesBeforeOpen),
  end: parseMarketHourMinute(marketHours?.close),
  endForHalfDay: parseMarketHourMinute(marketHours?.closeForHalfDay),
});

export const getTradingSessionConfig = (
  country: string,
  minutesBeforeOpen?: NullableNumber,
): MarketStartAndEndTime => buildTradingSessionConfig(MarketHoursState.get(country), minutesBeforeOpen);

/** Pre-market open to after-hours close - the window the enhanced 1D chart plots. */
export const getDailyChartSessionTimes = (country: string): MarketStartAndEndTime => {
  const marketHoursResponse = MarketHoursState.get(country);
  const config = buildTradingSessionConfig(marketHoursResponse, undefined);
  const { preMarketStart, afterMarketEnd, afterMarketEndForHalfDay } = marketHoursResponse.marketHours;

  return {
    ...config,
    start: preMarketStart ? parseMarketHourMinute(preMarketStart) : config.start,
    end: afterMarketEnd ? parseMarketHourMinute(afterMarketEnd) : config.end,
    endForHalfDay: afterMarketEndForHalfDay ? parseMarketHourMinute(afterMarketEndForHalfDay) : config.endForHalfDay,
  };
};

export const getStartAndEndDate = (dateAndTime: NullableString, country: string): StartAndEndDate => {
  const { timeZone, start, end, endForHalfDay, nonWorkingDays } = isEnhancedChartsEnabled()
    ? getDailyChartSessionTimes(country)
    : getTradingSessionConfig(country, undefined);
  const baseDate = (dateAndTime ? moment.utc(dateAndTime) : moment.utc()).tz(timeZone);
  const startMoment = buildMomentByDateAndTime(baseDate, timeZone, start.hour, start.minute);

  const dateString = baseDate.format(MARKET_DATA_DATE);
  const isHalfDay = nonWorkingDays.some(day => day.date === dateString && day.halfDay);

  const endTime = isHalfDay ? endForHalfDay : end;
  let endMoment = buildMomentByDateAndTime(baseDate, timeZone, endTime.hour, endTime.minute);

  const currDateMoment = moment().tz(timeZone);
  // Only apply current time logic if the input date is today
  if (currDateMoment.isSame(baseDate, 'day') && currDateMoment.isBetween(startMoment, endMoment)) {
    endMoment = buildMomentByDateAndTimeZone(baseDate, timeZone);
  }

  return { start: startMoment, end: endMoment };
};

export const getPreviousClosePriceDataSet = (
  symbol: string,
  symbolRows: OHLCV[],
): { data: OHLCV[]; previousCloseData?: OHLCV } => {
  // Get previous day's close from TradePriceCache
  const tradePrice = TradePriceCache.get(symbol);
  const prevClose = tradePrice?.priceData.lineClose;

  const previousCloseData = typeof prevClose === 'number'
    ? ({
      ...DEFAULT_OHLCV_DATA,
      symbol,
      close: prevClose,
      open: prevClose,
      high: prevClose,
      low: prevClose,
    } as OHLCV)
    : undefined;

  const data = isEmpty(symbolRows) && typeof prevClose === 'number' ? [ previousCloseData! ] : symbolRows;

  return { data, previousCloseData };
};

export const getStartDayForWeekAndMonthRanges = (
  shouldIncludeTodayData: boolean,
  currDate: Moment,
  timeZone: string,
  start: TimeOfDay,
  bucket: GWMarketDataSummaryBucket.OneWeek | GWMarketDataSummaryBucket.OneMonth,
): Moment => {
  if (bucket === GWMarketDataSummaryBucket.OneWeek) {
    const { AFTER_OPEN, BEFORE_OPEN } = BUCKET_LOOKBACK_CONFIG.WEEK;
    const dayCount = shouldIncludeTodayData ? AFTER_OPEN : BEFORE_OPEN;

    return buildMomentByDateAndTime(currDate.clone().subtract(dayCount, 'days'), timeZone, start.hour, start.minute);
  }

  // else - OneMonth
  return buildMomentByDateAndTime(
    currDate.clone().subtract(1, 'months').subtract(BUCKET_LOOKBACK_CONFIG.MONTH_OFFSET, 'days'),
    timeZone,
    start.hour,
    start.minute,
  );
};

export const fillMissingCandles = (
  data: OHLCV[],
  start: Moment,
  end: Moment,
  firstItemOrPreviousCloseData?: OHLCV,
  interval = 15, // minutes
): OHLCV[] => {
  if (data.length === 0) return [];

  const mappedData = convertOHLCVToMap(data, 'dateAndTime');

  const flooredEnd = end
    .clone()
    .seconds(0)
    .milliseconds(0)
    .subtract(end.minute() % interval, 'minutes');

  const filled: OHLCV[] = [];
  let lastKnown: OHLCV | null = null;

  for (let timeMoment = start.clone(); timeMoment.isSameOrBefore(flooredEnd); timeMoment.add(interval, 'minutes')) {
    const formatted = timeMoment.format(MARKET_DATA_TIME_AND_DATE3);

    const exactMatch = mappedData.get(formatted);
    if (exactMatch) {
      // Use real data
      lastKnown = exactMatch;
      filled.push(exactMatch);
    } else if (lastKnown) {
      // Fill gap by cloning last known candle
      filled.push(createFilledCandle(lastKnown, timeMoment));
    } else {
      // No data yet (gap at the start): use default template or first available candle as fallback
      const template = firstItemOrPreviousCloseData || data[0];
      filled.push(createFilledCandle(template, timeMoment));
    }
  }

  return filled;
};

export { getLatestDateAndTime };

export const fill1DayGaps = (rows: Record<string, OHLCV[]>, country: string): Record<string, OHLCV[]> => {
  const result: Record<string, OHLCV[]> = {};

  Object.entries(rows).forEach(([ symbol, symbolRows ]) => {
    const { data, previousCloseData } = getPreviousClosePriceDataSet(symbol, symbolRows);

    if (isEmpty(data)) {
      result[symbol] = [];
      return;
    }

    const windowAnchor = isEnhancedChartsEnabled() ? getLatestDateAndTime(data) : data[0].dateAndTime!;
    const { start, end } = getStartAndEndDate(windowAnchor, country);
    result[symbol] = fillMissingCandles(data, start, end, previousCloseData, 5);
  });

  return result;
};

export const getMinutesBeforeOpenByExchange = (
  bucket: GWMarketDataSummaryBucket.OneWeek | GWMarketDataSummaryBucket.OneMonth,
): NullableNumber => (
  bucket === GWMarketDataSummaryBucket.OneMonth ? MONTH_BUCKET_MINUTES_BEFORE_OPEN : undefined
);

type WeekAndMonthSession = {
  config: MarketStartAndEndTime;
  includesToday: boolean;
};

// US with enhanced charts: the summary already carries pre/after-hours candles, so 1W/1M use the extended session
export const getWeekAndMonthSession = (country: string, minutesBeforeOpen: NullableNumber): WeekAndMonthSession => {
  if (isEnhancedChartsEnabled()) {
    const config = getDailyChartSessionTimes(country);
    return { config, includesToday: hasSessionStartedToday(config) };
  }

  return {
    config: getTradingSessionConfig(country, minutesBeforeOpen),
    includesToday: shouldIncludeToday(country),
  };
};

export const fill1WeekAnd1MonthGaps = (
  rows: Record<string, OHLCV[]>,
  intervalMinutes: number,
  bucket: GWMarketDataSummaryBucket.OneWeek | GWMarketDataSummaryBucket.OneMonth,
  country: string,
): Record<string, OHLCV[]> => {
  const result: Record<string, OHLCV[]> = {};
  const minutesBeforeOpen = getMinutesBeforeOpenByExchange(bucket);
  const { config: tradingSessionConfig, includesToday: shouldIncludeTodayData } = getWeekAndMonthSession(
    country,
    minutesBeforeOpen,
  );
  const { timeZone, start, end } = tradingSessionConfig;

  Object.entries(rows).forEach(([ symbol, symbolRows ]) => {
    if (isEmpty(symbolRows)) {
      result[symbol] = [];
      return;
    }

    const filled: OHLCV[] = [];
    const currDate = moment.tz(timeZone);
    const endMoment = shouldIncludeTodayData
      ? buildMomentByDateAndTime(currDate, timeZone, end.hour, end.minute)
      : buildMomentByDateAndTime(currDate.clone().subtract(1, 'day'), timeZone, end.hour, end.minute);
    const startDayMoment = getStartDayForWeekAndMonthRanges(
      shouldIncludeTodayData,
      currDate,
      timeZone,
      start,
      bucket,
    );
    // Market time zone: the extended session ends 20:00 NY, which is already the next day in UTC
    const dayCursor = startDayMoment.clone().tz(timeZone);

    while (dayCursor.isSameOrBefore(endMoment, 'day')) {
      const isWorkingDay = isDateMarketDataWorkingDay(dayCursor.toDate(), true, country);
      if (!isWorkingDay) {
        // Skip weekends and non-working days
        dayCursor.add(1, 'day');
        continue;  
      }

      const dayMarketHours = getSessionHoursForDate(dayCursor, tradingSessionConfig);
      const dayStart = buildMomentByDateAndTime(
        dayCursor,
        timeZone,
        dayMarketHours.start.hour,
        dayMarketHours.start.minute,
      );

      let dayEnd = buildMomentByDateAndTime(dayCursor, timeZone, dayMarketHours.end.hour, dayMarketHours.end.minute);

      if (dayCursor.isSame(currDate, 'day') && shouldIncludeTodayData) {
        const isSessionRunning = currDate.isBetween(dayStart, dayEnd);
        if (isSessionRunning) {
          dayEnd = buildMomentByDateAndTimeZone(dayCursor, timeZone);
        }
      }

      const dailyFilled = fillMissingCandles(symbolRows, dayStart, dayEnd, undefined, intervalMinutes);
      filled.push(...dailyFilled);

      dayCursor.add(1, 'day');
    }

    result[symbol] = filled;
  });

  return result;
};

export const fill3MonthGaps = (rows: Record<string, OHLCV[]>, country: string): Record<string, OHLCV[]> => {
  const result: Record<string, OHLCV[]> = {};

  Object.entries(rows).forEach(([ symbol, symbolRows ]) => {
    if (symbolRows.length === 0) {
      result[symbol] = [];
      return;
    }

    const filled: OHLCV[] = [];
    const tradingSessionConfig = getTradingSessionConfig(country, undefined);
    const { timeZone, start, end } = tradingSessionConfig;

    const currDate = moment.tz(timeZone);
    const shouldIncludeTodayData = shouldIncludeToday(country);

    const endMoment = shouldIncludeTodayData
      ? buildMomentByDateAndTime(currDate, timeZone, end.hour, end.minute)
      : buildMomentByDateAndTime(currDate.clone().subtract(1, 'day'), timeZone, end.hour, end.minute);

    const startDay = buildMomentByDateAndTime(
      currDate
        .clone()
        .subtract(BUCKET_LOOKBACK_CONFIG.THREE_MONTHS.LOOKBACK_MONTHS, 'months'),
      timeZone,
      start.hour,
      start.minute,
    );

    const mappedData = convertOHLCVToMap(symbolRows, 'date');

    const dayCursor = startDay.clone();
    let lastKnownDailyCandle: OHLCV | null = null;

    while (dayCursor.isSameOrBefore(endMoment, 'day')) {
      const isWorkingDay = isDateMarketDataWorkingDay(dayCursor.toDate(), true, country);
      if (!isWorkingDay) {
        dayCursor.add(1, 'day');
        continue;  
      }

      const dayString = dayCursor.format(MARKET_DATA_DATE);
      const dayData = mappedData.get(dayString);

      if (dayData) {
        // Use real daily data
        lastKnownDailyCandle = dayData;
        filled.push(dayData);
      } else if (lastKnownDailyCandle) {
        // Fill gap with last known daily candle (volume = 0 for filled gaps)
        const filledDailyCandle: OHLCV = {
          ...lastKnownDailyCandle,
          volume: 0,
          date: dayString,
          time: '00:00:00.000',
          dateAndTime: `${dayString}T00:00:00`,
        };
        filled.push(filledDailyCandle);
      }
      // If no lastKnown yet, skip this day

      dayCursor.add(1, 'day');
    }

    result[symbol] = filled;
  });

  return result;
};

export const fill1YearGaps = (rows: Record<string, OHLCV[]>, country: string): Record<string, OHLCV[]> => {
  const result: Record<string, OHLCV[]> = {};

  Object.entries(rows).forEach(([ symbol, symbolRows ]) => {
    if (symbolRows.length === 0) {
      result[symbol] = [];
      return;
    }

    const filled: OHLCV[] = [];
    const tradingSessionConfig = getTradingSessionConfig(country, undefined);
    const { timeZone, start, end } = tradingSessionConfig;

    const currDate = moment.tz(timeZone);
    const shouldIncludeTodayData = shouldIncludeToday(country);

    const endMoment = shouldIncludeTodayData
      ? buildMomentByDateAndTime(currDate, timeZone, end.hour, end.minute)
      : buildMomentByDateAndTime(currDate.clone().subtract(1, 'day'), timeZone, end.hour, end.minute);

    const startDay = buildMomentByDateAndTime(
      currDate
        .clone()
        .subtract(BUCKET_LOOKBACK_CONFIG.ONE_YEAR.LOOKBACK_YEARS, 'years'),
      timeZone,
      start.hour,
      start.minute,
    );

    const weekCursor = getStartMondayForRange(symbolRows, timeZone, startDay);
    const mappedData = convertOHLCVToMap(symbolRows, 'date');
    let lastKnownWeeklyCandle: OHLCV | null = null;

    while (weekCursor.isSameOrBefore(endMoment, 'day')) {
      const weekString = weekCursor.format(MARKET_DATA_DATE);

      const weekData = mappedData.get(weekString);

      if (weekData) {
        // Use real weekly data
        lastKnownWeeklyCandle = weekData;
        filled.push(weekData);
      } else if (lastKnownWeeklyCandle) {
        // Fill gap with last known weekly candle (volume = 0 for filled gaps)
        const filledWeeklyCandle: OHLCV = {
          ...lastKnownWeeklyCandle,
          volume: 0,
          date: weekString,
          time: '00:00:00.000',
          dateAndTime: `${weekString}T00:00:00`,
        };
        filled.push(filledWeeklyCandle);
      }
      // If no lastKnown yet, skip this week

      // Move to next Monday (7 days later)
      weekCursor.add(YEAR_INTERVAL_DAYS, 'days');
    }

    result[symbol] = filled;
  });

  return result;
};

export const fill5YearGaps = (rows: Record<string, OHLCV[]>, country: string): Record<string, OHLCV[]> => {
  const result: Record<string, OHLCV[]> = {};

  Object.entries(rows).forEach(([ symbol, symbolRows ]) => {
    if (symbolRows.length === 0) {
      result[symbol] = [];
      return;
    }

    const filled: OHLCV[] = [];
    const tradingSessionConfig = getTradingSessionConfig(country, undefined);
    const { timeZone, start, end } = tradingSessionConfig;

    const currDate = moment.tz(timeZone);
    const shouldIncludeTodayData = shouldIncludeToday(country);

    const endMoment = shouldIncludeTodayData
      ? buildMomentByDateAndTime(currDate, timeZone, end.hour, end.minute)
      : buildMomentByDateAndTime(currDate.clone().subtract(1, 'day'), timeZone, end.hour, end.minute);

    const startDay = buildMomentByDateAndTime(
      currDate
        .clone()
        .subtract(BUCKET_LOOKBACK_CONFIG.FIVE_YEARS.LOOKBACK_YEARS, 'years'),
      timeZone,
      start.hour,
      start.minute,
    );

    const monthCursor = getWeeklyStartMonday(symbolRows, timeZone, startDay);
    const mappedData = convertOHLCVToMap(symbolRows, 'date');
    let lastKnownMonthlyCandle: OHLCV | null = null;

    while (monthCursor.isSameOrBefore(endMoment, 'month')) {
      const monthString = monthCursor.format(MARKET_DATA_DATE);
      const monthData = mappedData.get(monthString);

      if (monthData) {
        // Use real monthly data
        lastKnownMonthlyCandle = monthData;
        filled.push(monthData);
      } else if (lastKnownMonthlyCandle) {
        // Fill gap with last known monthly candle (volume = 0 for filled gaps)
        const filledMonthlyCandle: OHLCV = {
          ...lastKnownMonthlyCandle,
          volume: 0,
          date: monthString,
          time: '00:00:00.000',
          dateAndTime: `${monthString}T00:00:00`,
        };
        filled.push(filledMonthlyCandle);
      }
      // If no lastKnown yet, skip this month

      // Move to next month (first day of next month)
      monthCursor.add(1, 'month');
    }

    result[symbol] = filled;
  });

  return result;
};

export const fillMarketDataSummaryGaps = (
  rows: Record<string, OHLCV[]>,
  bucket: GWMarketDataSummaryBucket,
  country: string,
): Record<string, OHLCV[]> => {
  switch (bucket) {
    case GWMarketDataSummaryBucket.OneDay:
      return fill1DayGaps(rows, country);
    case GWMarketDataSummaryBucket.OneWeek:
      return fill1WeekAnd1MonthGaps(
        rows,
        WEEK_INTERVAL_MINUTES,
        GWMarketDataSummaryBucket.OneWeek,
        country,
      );
    case GWMarketDataSummaryBucket.OneMonth:
      return fill1WeekAnd1MonthGaps(
        rows,
        MONTH_INTERVAL_MINUTES,
        GWMarketDataSummaryBucket.OneMonth,
        country,
      );
    case GWMarketDataSummaryBucket.ThreeMonths:
      return fill3MonthGaps(rows, country);
    case GWMarketDataSummaryBucket.OneYear:
      return fill1YearGaps(rows, country);
    case GWMarketDataSummaryBucket.FiveYears:
      return fill5YearGaps(rows, country);
    default:
      return rows;
  }

  return rows;
};
